import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import api from '../services/api';

function SmartWatchSetup() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [platform, setPlatform] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProvider, setImportProvider] = useState('Upload');
  const [appleIntegrationKey, setAppleIntegrationKey] = useState('');
  const [appleImportUrl, setAppleImportUrl] = useState('');
  const [appleStatus, setAppleStatus] = useState(null);
  const [hasAutoSyncedGoogleFit, setHasAutoSyncedGoogleFit] = useState(false);
  // O Health Connect só existe dentro da app do ginásio: no browser, este cartão nem aparece.
  const [healthConnect, setHealthConnect] = useState(null);

  useEffect(() => {
    detectPlatform();
    loadConnectionStatus();
    loadWorkouts();
    loadAppleStatus();

    // Handle OAuth return result
    try {
      const params = new URLSearchParams(window.location.search);
      const googleFitStatus = params.get('googlefit');
      if (googleFitStatus === 'connected') {
        toast.success('Google Fit ligado! A sincronizar…');
        params.delete('googlefit');
        const newQs = params.toString();
        const newUrl = window.location.pathname + (newQs ? `?${newQs}` : '');
        window.history.replaceState({}, document.title, newUrl);
      } else if (googleFitStatus === 'error') {
        toast.error('Não foi possível ligar o Google Fit');
        params.delete('googlefit');
        const newQs = params.toString();
        const newUrl = window.location.pathname + (newQs ? `?${newQs}` : '');
        window.history.replaceState({}, document.title, newUrl);
      }
    } catch {
      // ignore
    }
  }, []);

  const detectPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      setPlatform('Android');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('iOS');
    } else {
      setPlatform('Desktop');
    }
  };

  /** O plugin nativo, quando esta página está a correr dentro da app Android. */
  const healthConnectPlugin = () => window.Capacitor?.Plugins?.HealthConnect || null;

  const loadHealthConnect = async () => {
    const plugin = healthConnectPlugin();
    if (!plugin) return;                       // browser: não há nada a mostrar
    try {
      setHealthConnect(await plugin.status());
    } catch {
      setHealthConnect({ available: false, granted: false, reason: 'erro' });
    }
  };

  /**
   * Pede as permissões e sincroniza. Reenviar treinos já enviados é seguro: o servidor
   * reconhece o mesmo treino pelo início, fim e tipo, e actualiza em vez de duplicar — por
   * isso não se guarda aqui nenhuma marca de "até onde já sincronizei", que se
   * dessincronizaria à primeira falha.
   */
  const handleSyncHealthConnect = async () => {
    const plugin = healthConnectPlugin();
    if (!plugin) return;

    setSyncing(true);
    try {
      let estado = await plugin.status();
      if (!estado.available) {
        toast.error('Este telemóvel não tem Health Connect disponível');
        setHealthConnect(estado);
        return;
      }

      if (!estado.granted) {
        const resposta = await plugin.requestHealthPermissions();
        if (!resposta?.granted) {
          toast.error('Sem permissão para ler os treinos');
          setHealthConnect(await plugin.status());
          return;
        }
        estado = await plugin.status();
      }
      setHealthConnect(estado);

      const { workouts: lidos } = await plugin.readWorkouts({ days: 30 });
      if (!lidos || lidos.length === 0) {
        toast.info('Não há treinos novos nos últimos 30 dias');
        return;
      }

      const res = await api.post('/healthintegration/healthconnect/import', { workouts: lidos });
      const { imported = 0, updated = 0 } = res.data || {};
      toast.success(
        imported > 0
          ? `${imported} treino(s) importado(s)` + (updated > 0 ? `, ${updated} actualizado(s)` : '')
          : 'Já estava tudo sincronizado'
      );

      await loadConnectionStatus();
      await loadWorkouts();
    } catch (error) {
      toast.error(error?.message || 'Não foi possível sincronizar os treinos');
    } finally {
      setSyncing(false);
    }
  };

  const loadConnectionStatus = async () => {
    try {
      const response = await api.get('/healthintegration/connection-status');
      setConnection(response.data);
      if (response.data?.provider) {
        setImportProvider(response.data.provider);
      }
    } catch (error) {
      // Sem conexão configurada
      setConnection(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAppleStatus = async () => {
    try {
      loadHealthConnect();
      const res = await api.get('/healthintegration/applehealth/status');
      setAppleStatus(res.data);
      if (res.data?.importUrl) setAppleImportUrl(res.data.importUrl);
    } catch {
      setAppleStatus(null);
    }
  };

  const loadWorkouts = async () => {
    try {
      const response = await api.get('/healthintegration/imported-workouts');
      setWorkouts(response.data);
    } catch (error) {
      console.error('Erro ao carregar treinos');
    }
  };

  const handleEnableHealthTracking = async () => {
    try {
      await api.patch('/users/me', { healthIntegrationEnabled: true });
      if (updateUser) {
        updateUser({ ...user, healthIntegrationEnabled: true });
      }
      toast.success('Monitorização de saúde ativada!');
    } catch (error) {
      toast.error('Erro ao ativar monitorização');
    }
  };

  const handleConnectGoogleFit = async () => {
    setSyncing(true);
    try {
      // Reduce steps: ensure monitoring is enabled before OAuth.
      if (!user?.healthIntegrationEnabled) {
        await api.patch('/users/me', { healthIntegrationEnabled: true });
        if (updateUser) updateUser({ ...user, healthIntegrationEnabled: true });
      }

      const res = await api.get('/healthintegration/googlefit/connect');
      const authUrl = res?.data?.authUrl;
      if (!authUrl) throw new Error('missing_authUrl');
      window.location.href = authUrl;
    } catch (error) {
      const msg = error?.response?.data?.error;
      if (msg === 'googlefit_disabled') toast.error('Google Fit não está ativo nas configurações');
      else if (msg === 'googlefit_redirect_invalid') toast.error('A ligação ao Google Fit ainda não está configurada neste ginásio. Contacte a receção.');
      else toast.error('Erro ao iniciar ligação Google Fit');
    } finally {
      setSyncing(false);
    }
  };

  const handleConnectAppleHealth = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/healthintegration/applehealth/create-key');
      const key = res?.data?.integrationKey;
      const url = res?.data?.importUrl;
      if (key) setAppleIntegrationKey(key);
      if (url) setAppleImportUrl(url);

      toast.success('Chave criada. Copia-a agora: só é mostrada desta vez.');
      await loadConnectionStatus();
      await loadAppleStatus();
      setImportProvider('AppleHealth');
    } catch (error) {
      toast.error('Erro ao conectar Apple Health');
    } finally {
      setSyncing(false);
    }
  };

  const appleFullUrl = appleImportUrl ? `${window.location.origin}${appleImportUrl}` : '';

  const copyToClipboard = async (value, label) => {
    try {
      if (!value) return;
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado!`);
    } catch {
      toast.info('Não foi possível copiar. Seleciona o texto e copia à mão.');
    }
  };

  const handleCopyAppleKey = () => copyToClipboard(appleIntegrationKey, 'Chave');

  const handleRegenerateAppleKey = async () => {
    if (!window.confirm('Gerar uma chave nova? A atual deixa de funcionar e terás de a substituir no Atalho.')) return;
    await handleConnectAppleHealth();
  };

  const handleRevokeAppleKey = async () => {
    if (!window.confirm('Revogar a chave? O Atalho deixa de conseguir enviar treinos.')) return;
    try {
      await api.post('/healthintegration/applehealth/revoke');
      setAppleIntegrationKey('');
      await loadAppleStatus();
      await loadConnectionStatus();
      toast.success('Chave revogada');
    } catch {
      toast.error('Não foi possível revogar a chave');
    }
  };

  const appleJsonExample = `{
  "workouts": [
    {
      "startAt": "2026-09-20T09:00:00+01:00",
      "endAt": "2026-09-20T10:05:00+01:00",
      "workoutType": "Running",
      "calories": 412,
      "distance": 5200
    }
  ]
}`;

  const handleGoogleFitSync = async () => {
    setSyncing(true);
    try {
      await api.post('/healthintegration/googlefit/sync');
      toast.success('Sincronização Google Fit concluída!');
      await loadWorkouts();
      await loadConnectionStatus();
    } catch (error) {
      toast.error('Erro ao sincronizar Google Fit');
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync Google Fit when connected and never synced or stale.
  useEffect(() => {
    if (!connection?.isConnected) return;
    if (connection?.provider !== 'GoogleFit') return;
    if (hasAutoSyncedGoogleFit) return;

    const last = connection?.lastSyncAt ? new Date(connection.lastSyncAt) : null;
    const isStale = !last || (Date.now() - last.getTime()) > (12 * 60 * 60 * 1000);
    if (!isStale) {
      setHasAutoSyncedGoogleFit(true);
      return;
    }

    setHasAutoSyncedGoogleFit(true);
    handleGoogleFitSync();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, hasAutoSyncedGoogleFit]);

  const handleDisconnect = async () => {
    if (window.confirm('Tem certeza que deseja desconectar?')) {
      try {
        if (connection?.provider === 'AppleHealth') {
          await api.post('/healthintegration/applehealth/revoke');
          setAppleIntegrationKey('');
          setAppleImportUrl('');
        } else {
          await api.post('/healthintegration/connect', {
            platform: connection?.platform || platform,
            provider: connection?.provider || 'Unknown',
            isConnected: false,
            lastSyncAt: null,
            permissionsJson: null
          });
        }
        toast.success('Dispositivo desconectado');
        setConnection(null);
      } catch (error) {
        toast.error('Erro ao desconectar');
      }
    }
  };

  const handleImportWorkoutFile = async () => {
    if (!importFile) {
      toast.info('Selecione um ficheiro (.gpx ou .tcx)');
      return;
    }

    setSyncing(true);
    try {
      const form = new FormData();
      form.append('file', importFile);
      form.append('sourceProvider', connection?.provider || importProvider || 'Upload');

      await api.post('/healthintegration/import-file', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Treino importado!');
      setImportFile(null);
      await loadWorkouts();

      // Atualizar última sincronização (apenas marcação interna)
      if (connection?.provider) {
        await api.post('/healthintegration/connect', {
          ...connection,
          lastSyncAt: new Date().toISOString()
        });
        loadConnectionStatus();
      }
    } catch (error) {
      const msg = error?.response?.data?.error;
      if (msg === 'unsupported_format') toast.error('Formato não suportado. Use .gpx ou .tcx');
      else if (msg === 'invalid_file') toast.error('Ficheiro inválido ou sem dados de treino');
      else toast.error('Erro ao importar treino');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ⌚ Configuração de Smartwatch
        </h2>

        {/* Plataforma Detectada */}
        <div className="info-box" style={{ 
          background: 'var(--gray-100)', 
          padding: '1rem', 
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p>
            <strong>Plataforma detectada:</strong> {platform}
            {platform === 'Desktop' && (
              <span style={{ color: 'var(--warning-text)', marginLeft: '0.5rem' }}>
                (Para melhor experiência, aceda pelo telemóvel)
              </span>
            )}
          </p>
        </div>

        {/* Estado da Monitorização */}
        {!user?.healthIntegrationEnabled ? (
          <div className="setup-section">
            <h3>📊 Ativar Monitorização de Treinos</h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Ative a monitorização para acompanhar calorias gastas, frequência cardíaca e mais.
            </p>
            <button className="btn btn-primary" onClick={handleEnableHealthTracking}>
              ✓ Ativar Monitorização
            </button>
          </div>
        ) : (
          <>
            {/* Conexão com Smartwatch */}
            <div className="setup-section" style={{ marginBottom: '2rem' }}>
              <h3>🔗 Conectar Smartwatch</h3>
              
              {connection?.isConnected ? (
                <div className="connected-status" style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '2px solid var(--secondary)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginTop: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '2rem' }}>
                      {connection.provider === 'AppleHealth' ? '🍎' : '🤖'}
                    </span>
                    <div>
                      <h4 style={{ margin: 0 }}>{connection.provider}</h4>
                      <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                        {connection.platform} • Conectado
                      </p>
                    </div>
                    <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                      ✓ Ativo
                    </span>
                  </div>
                  
                  {connection.lastSyncAt && (
                    <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
                      Última sincronização: {format(parseISO(connection.lastSyncAt), "dd/MM/yyyy 'às' HH:mm", { locale: pt })}
                    </p>
                  )}
                  
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {connection?.provider === 'GoogleFit' ? (
                      <button className="btn btn-primary" onClick={handleGoogleFitSync} disabled={syncing}>
                        {syncing ? '⏳ A sincronizar...' : '🔄 Sincronizar Agora'}
                      </button>
                    ) : (
                      <button className="btn btn-primary" onClick={loadWorkouts} disabled={syncing}>
                        {syncing ? '⏳ ...' : '↻ Atualizar'}
                      </button>
                    )}
                    <button className="btn btn-outline" onClick={handleDisconnect}>
                      Desconectar
                    </button>
                  </div>

                  {connection?.provider === 'AppleHealth' && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'rgba(99, 102, 241, 0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(99, 102, 241, 0.25)'
                    }}>
                      <strong>Apple Watch → Saúde no iPhone → Atalho → GyMag</strong>
                      <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                        O iPhone envia os treinos ao GyMag através de um Atalho. Só são enviados treinos:
                        início, fim, tipo, calorias e distância.
                      </div>

                      {appleStatus && (
                        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                          <div>Chave ativa: <strong>{appleStatus.hasActiveKey ? 'sim' : 'não'}</strong></div>
                          <div>Última sincronização: <strong>{appleStatus.lastSyncAt
                            ? format(parseISO(appleStatus.lastSyncAt), "d 'de' MMMM 'às' HH:mm", { locale: pt })
                            : 'ainda nenhuma'}</strong></div>
                          <div>Treinos importados: <strong>{appleStatus.importedWorkouts ?? 0}</strong></div>
                        </div>
                      )}

                      {appleIntegrationKey ? (
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                            <strong>Chave (só é mostrada agora)</strong>
                            <button className="btn btn-outline" onClick={handleCopyAppleKey}>Copiar chave</button>
                          </div>
                          <div style={{
                            marginTop: '0.5rem', padding: '0.75rem', borderRadius: '8px', background: 'white',
                            border: '1px solid var(--gray-200)', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85rem'
                          }}>
                            {appleIntegrationKey}
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                          A chave só aparece no momento em que é criada. Se a perderes, gera uma nova e
                          substitui-a no Atalho.
                        </div>
                      )}

                      {appleFullUrl && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                            <strong>Endereço para o Atalho</strong>
                            <button className="btn btn-outline" onClick={() => copyToClipboard(appleFullUrl, 'Endereço')}>
                              Copiar endereço
                            </button>
                          </div>
                          <div style={{
                            marginTop: '0.5rem', padding: '0.75rem', borderRadius: '8px', background: 'white',
                            border: '1px solid var(--gray-200)', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85rem'
                          }}>
                            {appleFullUrl}
                          </div>
                        </div>
                      )}

                      <details style={{ marginTop: '1rem' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Como criar o Atalho no iPhone</summary>
                        <ol style={{ marginTop: '0.5rem', paddingLeft: '1.2rem', fontSize: '0.88rem', lineHeight: 1.6 }}>
                          <li>Abre a app <strong>Atalhos</strong> e cria um atalho novo.</li>
                          <li>Adiciona <strong>Encontrar amostras de saúde</strong>: tipo <strong>Treinos</strong>,
                              filtro <em>Data de início</em> nos últimos 7 dias.</li>
                          <li>Adiciona <strong>Repetir com cada</strong> sobre o resultado.</li>
                          <li>Dentro do ciclo, usa <strong>Obter detalhes da amostra de saúde</strong> para ler
                              <em> Data de início</em> e <em>Data de fim</em>, e monta um <strong>Dicionário</strong> com
                              os campos <code>startAt</code>, <code>endAt</code> e, se os tiveres,
                              <code> workoutType</code>, <code>calories</code> e <code>distance</code>.</li>
                          <li>Junta cada dicionário a uma variável (por exemplo <em>treinos</em>) com
                              <strong> Adicionar à variável</strong>.</li>
                          <li>No fim, adiciona <strong>Obter conteúdo do URL</strong> com o endereço acima:
                              método <strong>POST</strong>, cabeçalho <code>X-Integration-Key</code> com a tua chave,
                              e corpo <strong>JSON</strong> com um campo <code>workouts</code> do tipo
                              <em> Array</em> igual à variável.</li>
                          <li>Corre o atalho uma vez e confirma aqui a última sincronização. Depois podes criar uma
                              automatização diária (a app Saúde exige o iPhone desbloqueado).</li>
                        </ol>
                        <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                          O corpo enviado fica assim:
                        </div>
                        <pre style={{
                          marginTop: '0.35rem', padding: '0.75rem', borderRadius: '8px', background: 'white',
                          border: '1px solid var(--gray-200)', fontSize: '0.8rem', overflowX: 'auto'
                        }}>{appleJsonExample}</pre>
                        <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                          Enviar o mesmo treino outra vez não o duplica: o GyMag reconhece-o e atualiza-o.
                        </div>
                      </details>

                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-outline" onClick={handleRegenerateAppleKey} disabled={syncing}>
                          Gerar chave nova
                        </button>
                        <button className="btn btn-outline" onClick={handleRevokeAppleKey}>
                          Revogar chave
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="connect-options" style={{ marginTop: '1rem' }}>
                  {/* Google Fit - Android */}
                  <div className="connect-card" style={{
                    border: '2px solid var(--gray-200)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }} onClick={handleConnectGoogleFit}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '2.5rem' }}>🤖</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>Google Fit</h4>
                        <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                          Para Android, Wear OS, Samsung Galaxy Watch
                        </p>
                      </div>
                      <button className="btn btn-primary" disabled={syncing}>
                        {syncing ? '...' : 'Conectar'}
                      </button>
                    </div>
                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                      <p>✓ Calorias gastas • ✓ Frequência cardíaca • ✓ Passos • ✓ Treinos</p>
                    </div>
                  </div>

                  {/* Health Connect — só dentro da app Android, que é onde o plugin existe */}
                  {healthConnect && (
                    <div className="connect-card" style={{
                      border: '2px solid var(--gray-200)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '1rem',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '2.5rem' }}>⌚</span>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: 0 }}>Relógio Android</h4>
                          <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                            {healthConnect.available
                              ? (healthConnect.granted
                                  ? 'Ligado ao Health Connect'
                                  : 'Pelo Health Connect, com a tua autorização')
                              : (healthConnect.reason === 'provider-update-required'
                                  ? 'O Health Connect deste telemóvel precisa de ser actualizado'
                                  : 'Este telemóvel não tem Health Connect')}
                          </p>
                        </div>
                        {healthConnect.available && (
                          <button className="btn btn-primary" disabled={syncing} onClick={handleSyncHealthConnect}>
                            {syncing ? '...' : (healthConnect.granted ? 'Sincronizar' : 'Ligar')}
                          </button>
                        )}
                      </div>
                      <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                        <p>✓ Treinos • ✓ Distância • ✓ Calorias — dos últimos 30 dias</p>
                      </div>
                    </div>
                  )}

                  {/* Apple Health - iOS */}
                  <div className="connect-card" style={{
                    border: '2px solid var(--gray-200)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }} onClick={handleConnectAppleHealth}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '2.5rem' }}>🍎</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>Apple Health</h4>
                        <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                          Para iPhone e Apple Watch, através de um Atalho
                        </p>
                      </div>
                      <button className="btn btn-primary" disabled={syncing}>
                        {syncing ? '...' : 'Conectar'}
                      </button>
                    </div>
                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                      <p>✓ Treinos (início, fim, tipo, calorias, distância)</p>
                    </div>
                  </div>

                  {/* Entrada Manual */}
                  <div className="connect-card" style={{
                    border: '2px dashed var(--gray-300)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    background: 'var(--gray-50)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '2.5rem' }}>✍️</span>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>Registo Manual</h4>
                        <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                          (Em breve) registe treinos manualmente
                        </p>
                      </div>
                      <span className="badge badge-secondary">Disponível</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Importar ficheiro real */}
            <div className="setup-section" style={{ marginBottom: '2rem' }}>
              <h3>📥 Importar treino (GPX/TCX)</h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
                Exporte o treino do relógio/app e carregue aqui (.gpx ou .tcx). Isto guarda o treino no seu histórico.
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="file"
                  accept=".gpx,.tcx"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
                <select
                  value={importProvider}
                  onChange={(e) => setImportProvider(e.target.value)}
                  style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--gray-300)' }}
                >
                  <option value="Upload">Upload</option>
                  <option value="GoogleFit">Google Fit</option>
                  <option value="AppleHealth">Apple Health</option>
                  <option value="Garmin">Garmin</option>
                  <option value="Strava">Strava</option>
                  <option value="Polar">Polar</option>
                </select>
                <button className="btn btn-primary" onClick={handleImportWorkoutFile} disabled={syncing || !importFile}>
                  {syncing ? '⏳ A importar...' : '📥 Importar'}
                </button>
              </div>
            </div>

            {/* Treinos Importados */}
            <div className="setup-section">
              <h3>📈 Histórico de Treinos</h3>
              
              {workouts.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: 'var(--gray-500)',
                  background: 'var(--gray-50)',
                  borderRadius: '8px',
                  marginTop: '1rem'
                }}>
                  <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</p>
                  <p>Ainda não tem treinos registados.</p>
                  <p style={{ fontSize: '0.85rem' }}>
                    Conecte o seu smartwatch ou registe treinos manualmente.
                  </p>
                </div>
              ) : (
                <div className="workouts-list" style={{ marginTop: '1rem' }}>
                  {workouts.slice(0, 10).map((workout) => (
                    <div key={workout.id} className="workout-item" style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '1rem',
                      borderBottom: '1px solid var(--gray-200)',
                      gap: '1rem'
                    }}>
                      <div className="workout-icon" style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        background: 'var(--primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}>
                        🏋️
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>{workout.workoutType || 'Treino'}</h4>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                          {format(parseISO(workout.startAt), "dd/MM/yyyy 'às' HH:mm", { locale: pt })}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '1.2rem', 
                          fontWeight: 'bold', 
                          color: 'var(--danger)' 
                        }}>
                          🔥 {workout.calories} kcal
                        </div>
                        {workout.distance && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                            📏 {(workout.distance / 1000).toFixed(2)} km
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Estatísticas Resumo */}
            {workouts.length > 0 && (
              <div className="stats-summary" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginTop: '2rem'
              }}>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {workouts.reduce((sum, w) => sum + (w.calories || 0), 0)}
                  </div>
                  <div style={{ opacity: 0.9 }}>🔥 Total Calorias</div>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {workouts.length}
                  </div>
                  <div style={{ opacity: 0.9 }}>🏋️ Treinos</div>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {Math.round(workouts.reduce((sum, w) => {
                      const start = new Date(w.startAt);
                      const end = new Date(w.endAt);
                      return sum + (end - start) / 60000;
                    }, 0))}
                  </div>
                  <div style={{ opacity: 0.9 }}>⏱️ Minutos</div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Instruções */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: 'var(--gray-50)', 
          borderRadius: '8px',
          fontSize: '0.9rem'
        }}>
          <h4>ℹ️ Como funciona?</h4>
          <ol style={{ paddingLeft: '1.5rem', color: 'var(--gray-600)' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>Ativar Monitorização:</strong> liga a funcionalidade na sua conta.
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>Importar ficheiro:</strong> exporte do relógio/app e carregue aqui (.gpx ou .tcx).
            </li>
            <li>
              Os treinos importados aparecem no seu histórico.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default SmartWatchSetup;
